import React, { useEffect, useState } from 'react';
import { FaSearch, FaUser, FaBell, FaPowerOff } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import { FaBarsStaggered } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';

function Header({ toggleSidebar, sidebarOpen }) {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [userAvatar, setUserAvatar] = useState("");
  const [userCompany, setUserCompany] = useState("");
  const [userRole, setUserRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);

  // Feature pages data
  const features = [
    { name: 'Dashboard', path: '/dashboard', aliases: ['home', 'overview', 'main', 'overview page', 'start', 'beranda', 'landing', 'utama'] },
    { name: 'Billing Reports', path: '/billing-reports', aliases: ['billing', 'invoice', 'payments', 'charges', 'billing history', 'payment report', 'financial report', 'tagihan', 'laporan tagihan', 'laporan billing'] },
    { name: 'Support Request', path: '/support-request', aliases: ['ticket', 'support', 'helpdesk', 'issue', 'trouble', 'customer support', 'problem report', 'bantuan', 'lapor masalah', 'request support'] },
    { name: 'Settings', path: '/settings', aliases: ['config', 'preferences', 'options', 'setup', 'account settings', 'pengaturan', 'konfigurasi', 'setting', 'pengaturan akun'] },
    { name: 'Add Users', path: '/add-users', aliases: ['new user', 'add user', 'create user', 'user management', 'add account', 'create account', 'tambah user', 'tambah akun', 'user baru', 'invite user'] },
    { name: 'User List', path: '/settings/user-list', aliases: ['users', 'list', 'user management', 'account list', 'user directory', 'daftar user', 'daftar akun', 'user list', 'semua user'] },
    { name: 'Group Details', path: '/groups/:group_id', aliases: ['group', 'group details', 'detail grup', 'kelompok', 'group info', 'group page'] },
    { name: 'Customer Management', path: '/customer-management', aliases: ['customer', 'customer management', 'pelanggan', 'kelola pelanggan', 'customer admin', 'manage customer'] },
    { name: 'Customer Detail', path: '/customer-management', aliases: ['customer detail', 'detail customer', 'customer info', 'detail pelanggan'] },
    { name: 'Group Management', path: '/group-management', aliases: ['group management', 'kelola grup', 'manage group', 'group admin', 'group manager'] },
    { name: 'User Management', path: '/user-management', aliases: ['user management', 'kelola user', 'manage user', 'admin user', 'user admin'] },
    { name: 'Project Management', path: '/project-management', aliases: ['project', 'project management', 'kelola project', 'manage project', 'proyek', 'project admin'] },
    { name: 'User Information', path: '/user-information', aliases: ['user info', 'informasi user', 'profile', 'user profile', 'akun', 'account info', 'user detail'] },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserName(user ? user.full_name : "User");
    setUserAvatar(user ? user.full_name.charAt(0).toUpperCase() : "U");
    setUserCompany(user ? user.company_name : "Unknown Company");
    setUserRole(user ? user.role : "Unknown Role");
  }, []);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() === "") {
      setSuggestions([]);
      return;
    }
    // Filter features based on search term in any alias
    const filtered = features.filter((feature) =>
      feature.name.toLowerCase().includes(value.toLowerCase()) || 
      feature.aliases.some(alias => alias.toLowerCase().includes(value.toLowerCase()))
    );
    setSuggestions(filtered);
  };  

  // Handle feature selection
  const handleSelectFeature = (path) => {
    navigate(path);
    setSearchTerm(""); // Clear search input after selection
    setSuggestions([]); // Clear suggestions
  };

  // Toggle user menu visibility
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  useEffect(() => {
    // Close dropdown if clicked outside
    const handleClickOutside = (e) => {
      if (userMenuOpen && !e.target.closest('.user-menu')) {
        setUserMenuOpen(false); // Close the dropdown if clicked outside
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
  
    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Close mobile overlays on route change or overlay close
  const closeMobileMenus = () => {
    setMobileSearchOpen(false);
    setMobileUserMenuOpen(false);
  };

  return (
    <div className={`flex items-center justify-between px-3.5 sm:px-6 py-2.5 sm:py-3.5 bg-white shadow-xs border-b border-gray-100 fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${sidebarOpen ? 'lg:pl-64' : 'lg:pl-0'}`}>
      {/* Sidebar toggle button */}
      <div className="flex items-center space-x-3 sm:space-x-6">
        <button onClick={toggleSidebar} className="text-gray-600 text-xl sm:text-2xl focus:outline-none" aria-label="Toggle navigation">
          {sidebarOpen ? <IoClose /> : <FaBarsStaggered />}
        </button>

        {/* Search Bar - Desktop */}
        <div className="relative ml-2 hidden md:block">
          <form className="flex items-center bg-gray-100 p-2 rounded-md w-full max-w-sm">
            <FaSearch className="text-gray-500 mr-2" />
            <input
              type="text"
              placeholder="Search for features..."
              className="bg-transparent outline-none w-full text-gray-700"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </form>
          {suggestions.length > 0 && (
            <ul className="absolute bg-white border border-gray-200 rounded-md w-full mt-1 shadow-lg z-50">
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => handleSelectFeature(suggestion.path)}
                  className="p-2 hover:bg-blue-100 cursor-pointer"
                >
                  {suggestion.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Search Icon - Mobile */}
        {/* <button
          className="md:hidden text-gray-600 text-2xl ml-2"
          onClick={() => setMobileSearchOpen(true)}
          aria-label="Open search"
        >
          <FaSearch />
        </button> */}
      </div>

      {/* Right icons */}
      <div className="flex items-center space-x-4">
        {/* Greeting - Desktop only */}
        <span className="text-gray-600 font-semibold text-lg hidden md:inline">
          Hi, <span className="text-blue-600">{userName}</span>
        </span>
        {/* User Icon - Desktop */}
        <button onClick={toggleUserMenu} className="text-gray-600 text-xl relative user-menu hidden md:inline-block">
          <FaUser />
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg rounded-lg border border-gray-200 p-4 z-auto">
              <div className="flex items-center space-x-4 mb-3">
                <div className="w-14 h-14 bg-blue-500 text-white flex items-center justify-center rounded-full text-2xl font-bold">
                  {userAvatar}
                </div>
                <div className="flex flex-col text-left">
                  <span className="font-semibold text-lg text-gray-900">{userName}</span>
                  <span className="text-xs text-gray-500 mt-1">{userRole}</span>
                  <span className="text-xs text-gray-500 mt-1">{userCompany}</span>
                </div>
              </div>
              <div className="border-t border-gray-200 my-3" />
              <div className="flex flex-col space-y-2">
                <button
                  className="inline-flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-blue-50 text-sm w-full font-medium"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/user-information");
                  }}
                >
                  <FaUser className="mr-2 w-4 h-4 text-blue-600" /> User Information
                </button>
                <button
                  className="inline-flex items-center px-3 py-2 rounded-md text-red-600 hover:bg-red-50 text-sm w-full font-medium"
                  onClick={() => {
                    localStorage.clear();
                    navigate("/login");
                  }}
                >
                  <FaPowerOff className="mr-2 w-4 h-4" /> Log Out
                </button>
              </div>
            </div>
          )}
        </button>
        {/* User Icon - Mobile */}
        <button
          className="md:hidden text-gray-600 text-lg sm:text-xl p-1"
          onClick={() => setMobileUserMenuOpen(true)}
          aria-label="Open user menu"
        >
          <FaUser />
        </button>
        <button className="text-gray-600 text-lg sm:text-xl p-1">
          <FaBell />
        </button>
      </div>

      {/* Mobile User Menu Drawer */}
      {mobileUserMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-start justify-center md:hidden" onClick={closeMobileMenus}>
          <div className="bg-white w-full max-w-xs mx-auto mt-10 rounded-lg shadow-lg p-4 relative" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-gray-500 text-2xl"
              onClick={closeMobileMenus}
              aria-label="Close user menu"
            >
              <IoClose />
            </button>
            <div className="flex items-center space-x-4 mb-3">
              <div className="w-12 h-12 bg-blue-500 text-white flex items-center justify-center rounded-full text-xl font-bold">
                {userAvatar}
              </div>
              <div className="flex flex-col text-left">
                <span className="font-semibold text-base text-gray-900">{userName}</span>
                <span className="text-xs text-gray-500 mt-1">{userRole}</span>
                <span className="text-xs text-gray-500 mt-1">{userCompany}</span>
              </div>
            </div>
            <div className="border-t border-gray-200 my-3" />
            <div className="flex flex-col space-y-2">
              <button
                className="inline-flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-blue-50 text-sm w-full font-medium"
                onClick={() => {
                  closeMobileMenus();
                  navigate("/user-information");
                }}
              >
                <FaUser className="mr-2 w-4 h-4 text-blue-600" /> User Information
              </button>
              <button
                className="inline-flex items-center px-3 py-2 rounded-md text-red-600 hover:bg-red-50 text-sm w-full font-medium"
                onClick={() => {
                  localStorage.clear();
                  navigate("/login");
                }}
              >
                <FaPowerOff className="mr-2 w-4 h-4" /> Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Header;
